/** English UI catalog — the reference shape; tr must match it key-for-key. */
export const en = {
  // App chrome
  'app.name': 'Vif',
  'app.tagline': 'Read faster than ever.',

  // Onboarding / calibration
  'onboarding.intro': "Let's find your ideal reading speed in about 30 seconds.",
  'onboarding.getStarted': 'Get Started',
  'onboarding.skip': 'Skip — I know my speed',
  'onboarding.round': 'Round {round} · {wpm} WPM',
  'onboarding.preparing': 'Preparing…',
  'onboarding.howWasThat': 'How was that?',
  'onboarding.tooFast': 'Too fast',
  'onboarding.justRight': 'Just right',
  'onboarding.tooSlow': 'Too slow',
  'onboarding.faster': 'Faster!',
  'onboarding.yourSpeed': 'Your comfortable reading speed. Adjust anytime with the arrow keys.',
  'onboarding.continue': 'Continue',
  'onboarding.tipsTitle': 'Quick Tips',
  'onboarding.tipOrpTitle': 'The Anchor Letter',
  'onboarding.tipOrpBody':
    'Each word highlights its Optimal Recognition Point. Your eye stays fixed.',
  'onboarding.tipKeysTitle': 'Keyboard Controls',
  'onboarding.tipKeysBody': 'Space to play/pause, ↑↓ for speed, ←→ to move by sentence.',
  'onboarding.tipWarmupTitle': 'Warm-Up',
  'onboarding.tipWarmupBody': 'Sessions start slow and ramp to your target speed.',
  'onboarding.tipDocsTitle': 'Documents',
  'onboarding.tipDocsBody': 'Drop a PDF, DOCX, EPUB, TXT/MD — or paste text directly.',
  'onboarding.startReading': 'Start Reading',

  // Library
  'library.title': 'Library',
  'library.empty': 'Import a document to start reading.',
  'library.import': 'Import',
  'library.paste': 'Paste text',
  'library.dropHint': 'PDF · DOCX · EPUB · TXT · MD',
  'library.progress': '{percent}% read',
  'library.timeLeft': '{minutes} min left',
  'library.lastRead': 'Last read {date}',
  'library.never': 'Not started',
  'library.delete': 'Delete',
  'library.deleteConfirm':
    'Delete "{title}" ({size})? Its reading position and bookmarks go with it.',
  'library.sort.recent': 'Recent',
  'library.sort.progress': 'Progress',
  'library.sort.title': 'Title',
  'library.slotCap':
    'Web beta supports up to 5 library documents. The native apps add Pro unlock for unlimited library.',
  'library.slotCapTitle': 'Library is full',
  'library.slotsUsed': '{used} of {max} slots used',
  'library.legacyResume': 'Restored your old reading position.',

  // Paste modal
  'paste.title': 'Paste text',
  'paste.placeholder': 'Paste anything — articles, chapters, notes…',
  'paste.start': 'Start Reading',
  'paste.documentTitle': 'Pasted text',

  // Import
  'import.parsing': 'Preparing "{name}"…',
  'import.page': 'Page {page} of {total}',
  'import.cancel': 'Cancel',
  'import.duplicate': 'Already in your library — opening it.',
  'import.failed': 'Import failed',

  // Reader
  'reader.resume': 'Resume from word {word}?',
  'reader.resumeYes': 'Resume',
  'reader.resumeNo': 'Start over',
  'reader.finished': 'Finished',
  'reader.wpm': 'WPM',
  'reader.chapter': 'Chapter',
  'reader.contents': 'Contents',
  'reader.bookmarks': 'Bookmarks',
  'reader.bookmarkAdded': 'Bookmarked',
  'reader.timeLeft': '{minutes} min',
  'reader.back': 'Library',
  'reader.playPause': 'Play or pause',
  'reader.seek': 'Reading position',
  'reader.rsvpLabel': 'Rapid serial word display — one word at a time at your reading speed',

  // Settings
  'settings.title': 'Settings',
  'settings.theme': 'Theme',
  'settings.theme.dark': 'Dark',
  'settings.theme.light': 'Light',
  'settings.theme.amoled': 'AMOLED',
  'settings.fontSize': 'Word size',
  'settings.font': 'Reading font',
  'settings.font.mono': 'JetBrains Mono',
  'settings.font.plex': 'Vif Neue',
  'settings.font.code': 'Vif Code',
  'settings.font.dyslexic': 'Vif Dyslexic',
  'settings.fontNote': 'The dyslexic-friendly font is best-effort; the anchor may drift ≤1 px.',
  'settings.fontDerivation':
    'Vif Neue derives from IBM Plex Mono, Vif Code from Source Code Pro, Vif Dyslexic from OpenDyslexic (renamed as their licenses require).',
  'reader.quickSettings': 'Quick settings',
  'settings.orpColor': 'Anchor color',
  'settings.showGuides': 'Guide notches',
  'settings.targetWpm': 'Reading speed',
  'settings.recalibrate': 'Recalibrate',
  'settings.adaptive': 'Adaptive word timing',
  'settings.warmup': 'Warm-up',
  'settings.warmupDuration': 'Warm-up duration',
  'settings.warmupStart': 'Warm-up start speed',
  'settings.chunk': 'Words per flash',
  'settings.chunkLayout': 'Chunk layout',
  'settings.chunkLayout.vertical': 'Stacked',
  'settings.chunkLayout.horizontal': 'Side by side',
  'settings.appLanguage': 'App language',
  'settings.docLanguage': 'Document language',
  'settings.docLanguage.auto': 'Auto ({detected})',
  'settings.stats': 'Stats',
  'settings.stats.session': 'This session',
  'settings.stats.words': 'Words',
  'settings.stats.time': 'Time',
  'settings.stats.avgWpm': 'Avg WPM',
  'settings.stats.total': 'All time',
  'settings.stats.sessions': 'Sessions',
  'settings.backup': 'Backup',
  'settings.export': 'Export library (.vif)',
  'settings.import': 'Import backup…',
  'settings.importConfirm':
    'Importing a backup replaces your whole library, positions and settings. Continue?',
  'settings.importDone': 'Backup restored.',
  'settings.seconds': '{n} s',

  // Durability banner (request, never guarantee — PRD §10)
  'durability.banner':
    "Your library lives in this browser's storage, granted at its discretion — it can be cleared if space runs low. Export is your backup.",
  'durability.install': 'Install Vif',
  'durability.export': 'Export now',

  // Generic
  'common.cancel': 'Cancel',
  'common.ok': 'OK',
  'common.close': 'Close',
  'common.on': 'On',
  'common.off': 'Off',
} as const;

export type MessageKey = keyof typeof en;
export type Catalog = Readonly<Record<MessageKey, string>>;
